import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, ArrowLeft, ArrowRight, CheckCircle2,
  Loader2, AlertTriangle, X,
} from "lucide-react";

const IGNORE = "__ignore__";

export default function ProductImport() {
  const nav = useNavigate();
  const [step, setStep] = useState("upload"); // upload | mapping | summary | result
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const [sessionId, setSessionId] = useState(null);
  const [columns, setColumns] = useState([]);
  const [fields, setFields] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [mapping, setMapping] = useState({}); // { colIndex: fieldKey }

  const [preview, setPreview] = useState(null);
  const [overwrite, setOverwrite] = useState("no");
  const [result, setResult] = useState(null);

  const usedFields = useMemo(
    () => new Set(Object.values(mapping).filter(v => v && v !== IGNORE)),
    [mapping]);

  const codeMapped = Object.values(mapping).includes("code");

  const autoMap = (cols, flds) => {
    const norm = (s) => (s || "").toString().trim().toLowerCase();
    const byLabel = {}; const byKey = {};
    flds.forEach(f => { byLabel[norm(f.label)] = f.key; byKey[norm(f.key)] = f.key; });
    // A few common Italian header aliases.
    const alias = {
      "codice": "code", "codice prodotto": "code", "cod": "code", "sku": "code",
      "nome": "name", "nome prodotto": "name", "prodotto": "name",
      "descrizione": "description_richtext", "categoria": "category",
      "sottocategoria": "subcategory", "sotto-categoria": "subcategory",
      "marca": "brand", "brand": "brand", "fornitore": "supplier",
      "codice fornitore": "supplier_raw_material_code",
      "prezzo": "price_value", "valuta": "price_currency",
      "quantita": "quantity_value", "quantità": "quantity_value",
      "udm": "udm", "barcode": "barcode_number", "ean": "barcode_number",
    };
    const next = {};
    cols.forEach(c => {
      const n = norm(c.name);
      const key = byLabel[n] || byKey[n] || alias[n];
      if (key && !Object.values(next).includes(key)) next[c.index] = key;
    });
    return next;
  };

  const onUpload = async () => {
    if (!file) { toast.error("Seleziona un file .xlsx o .csv"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/products/import/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setSessionId(r.data.session_id);
      setColumns(r.data.columns);
      setFields(r.data.fields);
      setTotalRows(r.data.total_rows);
      setMapping(autoMap(r.data.columns, r.data.fields));
      setStep("mapping");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il caricamento del file");
    } finally { setBusy(false); }
  };

  const onAnalyze = async () => {
    if (!codeMapped) { toast.error("Devi mappare il campo obbligatorio 'Codice prodotto'"); return; }
    setBusy(true);
    try {
      const r = await api.post("/products/import/preview", { session_id: sessionId, mapping });
      setPreview(r.data);
      setOverwrite("no");
      setStep("summary");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'analisi");
    } finally { setBusy(false); }
  };

  const onCommit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/products/import/commit", {
        session_id: sessionId, mapping, overwrite: overwrite === "yes",
      });
      setResult(r.data);
      setStep("result");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'importazione");
    } finally { setBusy(false); }
  };

  const StepDot = ({ n, label, active, done }) => (
    <div className="flex items-center gap-2">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] border
        ${done ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
          : active ? "border-[#1A1A1A] text-[#1A1A1A]" : "border-[#D8D2C6] text-[#B0B0B0]"}`}>
        {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : n}
      </div>
      <span className={`text-[10px] tracking-luxury uppercase ${active || done ? "text-[#1A1A1A]" : "text-[#B0B0B0]"}`}>{label}</span>
    </div>
  );

  return (
    <div className="p-4 pt-16 md:p-8 lg:p-12" data-testid="product-import-page">
      <button onClick={() => nav("/products")}
              className="inline-flex items-center gap-1.5 text-[10px] tracking-luxury text-[#707070] uppercase hover:text-[#1A1A1A] mb-4"
              data-testid="import-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Torna ai prodotti
      </button>
      <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">Anagrafica</div>
      <h1 className="font-serif text-3xl md:text-4xl mb-6">Importa prodotti da Excel</h1>

      <div className="flex items-center gap-4 md:gap-8 mb-8 flex-wrap">
        <StepDot n={1} label="Carica file" active={step === "upload"} done={["mapping", "summary", "result"].includes(step)} />
        <ArrowRight className="w-3.5 h-3.5 text-[#D8D2C6]" />
        <StepDot n={2} label="Mappatura" active={step === "mapping"} done={["summary", "result"].includes(step)} />
        <ArrowRight className="w-3.5 h-3.5 text-[#D8D2C6]" />
        <StepDot n={3} label="Riepilogo" active={step === "summary"} done={step === "result"} />
        <ArrowRight className="w-3.5 h-3.5 text-[#D8D2C6]" />
        <StepDot n={4} label="Esito" active={step === "result"} done={false} />
      </div>

      {/* ---------------- STEP 1: UPLOAD ---------------- */}
      {step === "upload" && (
        <div className="luxury-card p-8 max-w-2xl" data-testid="import-step-upload">
          <label className="block border-2 border-dashed border-[#D8D2C6] hover:border-[#C5B39B] transition cursor-pointer p-10 text-center"
                 data-testid="import-dropzone">
            <input type="file" accept=".xlsx,.csv" className="hidden"
                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                   data-testid="import-file-input" />
            <FileSpreadsheet className="w-10 h-10 mx-auto text-[#C5B39B] mb-3" strokeWidth={1.3} />
            {file ? (
              <div className="text-sm text-[#1A1A1A] font-medium">{file.name}</div>
            ) : (
              <>
                <div className="text-sm text-[#1A1A1A]">Trascina qui il file oppure clicca per selezionarlo</div>
                <div className="text-xs text-[#707070] mt-1">Formati supportati: .xlsx, .csv · max 5 MB</div>
              </>
            )}
          </label>
          <div className="mt-4 p-3 bg-[#FBF6EF] border-l-2 border-[#C5B39B] text-xs text-[#7A6A4A] leading-relaxed">
            La prima riga del file deve contenere le intestazioni delle colonne. Verranno gestiti solo i campi testuali e numerici;
            immagini, allegati e lista INCI sono ignorati.
          </div>
          <div className="flex justify-end mt-6">
            <button onClick={onUpload} disabled={!file || busy}
                    className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid="import-upload-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Carica e continua
            </button>
          </div>
        </div>
      )}

      {/* ---------------- STEP 2: MAPPING ---------------- */}
      {step === "mapping" && (
        <div className="luxury-card p-4 md:p-8" data-testid="import-step-mapping">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="text-sm text-[#707070]">
              {columns.length} colonne · {totalRows} righe. Associa ogni colonna del file a un campo dell'anagrafica.
            </div>
            <div className={`text-xs px-2.5 py-1 rounded-full border ${codeMapped ? "border-[#CFE4D6] bg-[#EAF3EC] text-[#2F6B45]" : "border-[#E3C9C9] bg-[#F7ECEC] text-[#8C3A3A]"}`}>
              {codeMapped ? "Codice prodotto mappato" : "Codice prodotto obbligatorio"}
            </div>
          </div>
          <div className="border border-[#E5E5E0] max-h-[52vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-[#E5E5E0] text-left text-[10px] tracking-luxury text-[#707070] uppercase">
                  <th className="px-3 py-2 font-normal bg-white">Colonna nel file</th>
                  <th className="px-3 py-2 font-normal bg-white">Esempi</th>
                  <th className="px-3 py-2 font-normal bg-white w-72">Campo di destinazione</th>
                </tr>
              </thead>
              <tbody>
                {columns.map(col => (
                  <tr key={col.index} className="border-b border-[#F0F0EA]" data-testid={`mapping-row-${col.index}`}>
                    <td className="px-3 py-2 text-[#1A1A1A] font-medium">{col.name}</td>
                    <td className="px-3 py-2 text-[#707070] text-xs">
                      {(col.samples || []).slice(0, 3).join(" · ") || <span className="text-[#B0B0B0]">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <select className="input-luxury !py-1.5 text-sm w-full"
                              value={mapping[col.index] || IGNORE}
                              onChange={(e) => setMapping(m => ({ ...m, [col.index]: e.target.value }))}
                              data-testid={`mapping-select-${col.index}`}>
                        <option value={IGNORE}>Ignora questa colonna</option>
                        {fields.map(f => (
                          <option key={f.key} value={f.key}
                                  disabled={usedFields.has(f.key) && mapping[col.index] !== f.key}>
                            {f.label}{f.required ? " *" : ""}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between mt-6">
            <button onClick={() => setStep("upload")} className="btn-ghost text-sm" data-testid="mapping-back">Indietro</button>
            <button onClick={onAnalyze} disabled={busy || !codeMapped}
                    className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid="import-analyze-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
              Analizza
            </button>
          </div>
        </div>
      )}

      {/* ---------------- STEP 3: SUMMARY ---------------- */}
      {step === "summary" && preview && (
        <div className="luxury-card p-4 md:p-8 max-w-3xl" data-testid="import-step-summary">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 border border-[#E5E5E0]">
              <div className="text-3xl font-serif text-[#2F6B45]" data-testid="summary-new">{preview.new_count}</div>
              <div className="text-[10px] tracking-luxury uppercase text-[#707070] mt-1">Nuovi prodotti</div>
            </div>
            <div className="p-4 border border-[#E5E5E0]">
              <div className="text-3xl font-serif text-[#B45309]" data-testid="summary-diff">{preview.existing_diff_count}</div>
              <div className="text-[10px] tracking-luxury uppercase text-[#707070] mt-1">Esistenti con dati diversi</div>
            </div>
            <div className="p-4 border border-[#E5E5E0]">
              <div className="text-3xl font-serif text-[#707070]" data-testid="summary-same">{preview.existing_same_count}</div>
              <div className="text-[10px] tracking-luxury uppercase text-[#707070] mt-1">Esistenti invariati</div>
            </div>
            <div className="p-4 border border-[#E5E5E0]">
              <div className="text-3xl font-serif text-[#707070]" data-testid="summary-skipped">{preview.skipped_no_code}</div>
              <div className="text-[10px] tracking-luxury uppercase text-[#707070] mt-1">Righe senza codice</div>
            </div>
          </div>

          {preview.duplicate_codes_in_file > 0 && (
            <div className="mb-4 p-3 bg-[#FBF6EF] border-l-2 border-[#C5B39B] text-xs text-[#7A6A4A] inline-flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>Il file contiene {preview.duplicate_codes_in_file} codici duplicati: verrà considerata l'ultima riga per ogni codice.</span>
            </div>
          )}

          {preview.diff_samples?.length > 0 && (
            <div className="mb-6">
              <div className="text-[10px] tracking-luxury uppercase text-[#707070] mb-2">Anteprima modifiche (primi {preview.diff_samples.length})</div>
              <div className="border border-[#E5E5E0] max-h-[28vh] overflow-y-auto divide-y divide-[#F0F0EA]">
                {preview.diff_samples.map((d, i) => (
                  <div key={i} className="px-3 py-2 text-sm" data-testid={`diff-sample-${i}`}>
                    <span className="font-mono text-xs text-[#707070]">{d.code}</span> — <span className="text-[#1A1A1A]">{d.name}</span>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {d.changes.map((c, j) => (
                        <span key={j} className="text-[11px] px-2 py-0.5 bg-[#F4F4F0] border border-[#E5E5E0] rounded-full">
                          {c.field}: <span className="text-[#8C3A3A] line-through">{String(c.old ?? "—") || "—"}</span> → <span className="text-[#2F6B45]">{String(c.new ?? "—") || "—"}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {preview.existing_diff_count > 0 ? (
            <div className="p-4 bg-[#FAF9F6] border-l-2 border-[#C5B39B] mb-6">
              <div className="text-sm text-[#1A1A1A] font-medium mb-3">
                Vuoi sovrascrivere i dati dei prodotti esistenti con le informazioni del file?
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-3 text-sm cursor-pointer" data-testid="overwrite-no">
                  <input type="radio" name="ow" checked={overwrite === "no"} onChange={() => setOverwrite("no")} />
                  <span>No — importa <strong>solo i {preview.new_count} nuovi prodotti</strong> (gli esistenti restano invariati)</span>
                </label>
                <label className="flex items-center gap-3 text-sm cursor-pointer" data-testid="overwrite-yes">
                  <input type="radio" name="ow" checked={overwrite === "yes"} onChange={() => setOverwrite("yes")} />
                  <span>Sì — importa i nuovi <strong>e aggiorna i {preview.existing_diff_count} esistenti</strong> con i dati del file</span>
                </label>
              </div>
            </div>
          ) : (
            <div className="mb-6 text-sm text-[#707070]">
              Nessun prodotto esistente da aggiornare. Verranno importati {preview.new_count} nuovi prodotti.
            </div>
          )}

          <div className="flex justify-between">
            <button onClick={() => setStep("mapping")} className="btn-ghost text-sm" data-testid="summary-back">Indietro</button>
            <button onClick={onCommit} disabled={busy || (preview.new_count === 0 && !(overwrite === "yes" && preview.existing_diff_count > 0))}
                    className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid="import-commit-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {busy ? "Importazione in corso..." : "Importa"}
            </button>
          </div>
        </div>
      )}

      {/* ---------------- STEP 4: RESULT ---------------- */}
      {step === "result" && result && (
        <div className="luxury-card p-8 max-w-2xl text-center" data-testid="import-result">
          <CheckCircle2 className="w-12 h-12 mx-auto text-[#2F6B45] mb-4" strokeWidth={1.3} />
          <h2 className="font-serif text-2xl mb-2">Importazione completata</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 my-6 text-left">
            <ResultStat label="Prodotti creati" value={result.created} testId="result-created" />
            <ResultStat label="Prodotti aggiornati" value={result.updated} testId="result-updated" />
            <ResultStat label="Righe saltate" value={result.skipped} testId="result-skipped" />
            <ResultStat label="Brand creati" value={result.created_brands} />
            <ResultStat label="Categorie create" value={result.created_categories} />
            <ResultStat label="Sottocategorie create" value={result.created_subcategories} />
            <ResultStat label="Fornitori creati" value={result.created_suppliers} />
          </div>
          <div className="text-xs text-[#707070] mb-6 max-w-md mx-auto">
            Il codice QR dei prodotti importati verrà generato quando apri il prodotto o tramite il pulsante "Rigenera" nella lista.
          </div>
          <button onClick={() => nav("/products")} className="btn-primary text-sm" data-testid="result-goto-products">
            Vai ai prodotti
          </button>
        </div>
      )}
    </div>
  );
}

const ResultStat = ({ label, value, testId }) => (
  <div className="p-3 border border-[#E5E5E0]" data-testid={testId}>
    <div className="text-2xl font-serif text-[#1A1A1A]">{value ?? 0}</div>
    <div className="text-[10px] tracking-luxury uppercase text-[#707070] mt-1">{label}</div>
  </div>
);
